import { useState, useEffect } from 'react';
import { getValidToken } from '../utils/auth';
import { optionTradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function formatDate(iso) {
  // trade_date arrives as YYYY-MM-DD; show DD-MM-YYYY per the user's example
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

// Historical simulation results (cmd/cheapbacktest) - static data, fetched
// once, columns exactly as the user specified 2026-07-11.
export default function BacktestTable({ variant, title, subtitle }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/cheap-backtest?variant=${variant}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (mounted) { setData(json); setError(null); }
      } catch (err) {
        if (mounted) setError(err.message);
      }
    })();
    return () => { mounted = false; };
  }, [variant]);

  const trades = data?.trades || [];

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          {title}
        </span>
        {data && (
          <>
            <span className="text-xs font-mono text-gray-400">
              {data.wins}/{data.trade_count} wins
            </span>
            <span className="text-xs font-mono" style={{ color: data.final_budget >= 500 ? '#22c55e' : '#f59e0b' }}>
              Budget: $500 → ${data.final_budget.toFixed(0)}
            </span>
            <span className="text-xs font-mono" style={{ color: data.saved > 0 ? '#22c55e' : '#6b7280' }}>
              Saved: ${data.saved.toFixed(0)}
            </span>
            {data.stopped_early && (
              <span className="text-xs px-2 py-1 rounded font-semibold"
                style={{ backgroundColor: '#ef444422', color: '#ef4444' }}>
                STOPPED EARLY — BUDGET FLOOR
              </span>
            )}
          </>
        )}
      </div>
      <div className="text-xs text-gray-500 mb-3">{subtitle}</div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}

      <div className="overflow-x-auto" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead className="sticky top-0" style={{ backgroundColor: '#111827' }}>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {['Date', 'Type', 'Strike', 'Buy Price', 'Sell Price', '# of contracts', 'Entry Time', 'Exit Time', 'Total amount', 'Total Gain/Loss', '% Gain/Loss'].map((h) => (
                <th key={h} className={`py-2 pr-3 font-medium ${h === 'Date' || h === 'Type' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr><td colSpan={11} className="py-4 text-center text-gray-600">No trades in the tested range</td></tr>
            ) : trades.map((t, i) => {
              const color = t.pnl >= 0 ? '#22c55e' : '#ef4444';
              return (
                <tr key={`${t.symbol}-${t.entry_at}-${i}`}
                  className="border-b hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                  <td className="py-1.5 pr-3 text-gray-300">{formatDate(t.trade_date)}</td>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: (t.type === 'C' ? '#22c55e' : '#ef4444') + '22', color: t.type === 'C' ? '#22c55e' : '#ef4444' }}>
                      {t.type === 'C' ? 'CALL' : 'PUT'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono">
                    <a href={optionTradingViewUrl(t.symbol)} target="_blank" rel="noopener noreferrer"
                      title="Open on TradingView"
                      style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {t.strike % 1 === 0 ? t.strike.toFixed(0) : t.strike.toFixed(1)}
                    </a>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.buy_price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.sell_price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{t.qty}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.entry_at)}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.exit_at)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.total_cost.toFixed(0)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color }}>
                    {t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(0)}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color }}>
                    {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
